import type { OfflineScene } from "@novorender/api";

import { log } from "utils/log";

const l = log.miniMap.tiled;

/** Subfolder tiled minimap files live under, inside the SDK's OPFS scene directory. */
const MINIMAP_DIR_NAME = "minimap";

/**
 * Journal-unsafe characters, mirroring the SDK's own `isJournalSafeName`: the
 * journal is unescaped `name,size` text, so a comma or control character in a
 * stored name corrupts every entry after it.
 */
function hasJournalUnsafeChars(name: string): boolean {
    for (let i = 0; i < name.length; i++) {
        const code = name.charCodeAt(i);
        if (code <= 0x1f || name[i] === ",") {
            return true;
        }
    }
    return false;
}

type MinimapOfflineContext = {
    /**
     * Lazy on purpose: incremental caching arms (`addScene`) after this
     * registry is set up, so the scene may not exist yet at registration time.
     */
    getScene: () => OfflineScene | undefined;
    /** Whether OPFS hits/misses may be teed into new writes this session. */
    writeEnabled: boolean;
    /** Whether OPFS may be read at all (covers fully-synced scenes even when writes are kill-switched). */
    readEnabled: boolean;
};

let context: MinimapOfflineContext | undefined;

/** Arm the tiled minimap route's OPFS read-through. `undefined` = network-only, matching pre-offline behavior. */
export function registerMinimapOfflineStore(ctx: MinimapOfflineContext): void {
    context = ctx;
}

export function unregisterMinimapOfflineStore(): void {
    context = undefined;
}

/** Test-only: replace/inspect the active registration without importing the effect that normally sets it. */
export function getMinimapOfflineContext(): MinimapOfflineContext | undefined {
    return context;
}

/** Test-only: reset the quota latch and pending-write backlog between tests. */
export function resetMinimapOfflineQuotaState(): void {
    writesDisabledByQuota = false;
    pendingWrites.clear();
}

// Set once per session on a quota error, so a full disk doesn't retry a write on every tile.
let writesDisabledByQuota = false;

/**
 * Writes that arrived before `context.getScene()` had a scene to write into
 * yet (e.g. a cold Procore load: mini map fetches can start before the
 * sibling `incrementalSync` effect's `addScene` resolves), keyed by OPFS
 * path so a tile requested again before the scene appears only queues once.
 * Flushed opportunistically the next time a read or write finds a scene --
 * without this, a file fetched during that window is silently never
 * written, since `TileBitmapCache`/`fetchPreviewBitmap` cache the decoded
 * bitmap in memory and never fetch that URL again this session.
 */
const pendingWrites = new Map<string, ArrayBuffer>();
/** Bound the backlog in case the scene never appears this session at all. */
const MAX_PENDING_WRITES = 64;

type FileAccessReporter = (filename: string, size: number, options?: { retain?: boolean }) => void;

/**
 * Tell the SDK's eviction loop this file was just read or written, so it is
 * budgeted and treated as recently used, rather than only discovered -- cold --
 * at the loop's next journal scan. `retain` asks for a file the loop doesn't
 * know yet to enter its main queue directly: tiles are small and re-read only
 * once per time the map is opened, so under heavy geometry churn they'd
 * otherwise take an evict → re-download round trip before settling in.
 *
 * `OfflineScene.reportOfflineFileAccess` arrives with `@novorender/api` 2.3.23
 * (BM-13747). Looked up dynamically so this compiles against the currently
 * pinned SDK and is simply a no-op there; it activates with the bump
 * (BM-13748) without a change here.
 */
function reportMinimapFileAccess(scene: OfflineScene, path: string, size: number): void {
    const report = (scene as unknown as { reportOfflineFileAccess?: FileAccessReporter }).reportOfflineFileAccess;
    if (typeof report === "function") {
        report.call(scene, path, size, { retain: true });
    }
}

function performWrite(scene: OfflineScene, path: string, buffer: ArrayBuffer): void {
    // Capture before the write: `dir.write` transfers the buffer to the SDK's
    // IO worker, after which `buffer.byteLength` reads as 0 on this thread.
    const size = buffer.byteLength;
    // Both handlers on the write promise itself (two-argument `then`), not
    // `.then(report).catch(...)`: the extra hop would delay the quota latch
    // below by a microtask, letting a write racing in right behind a
    // QuotaExceededError slip through before it flips.
    void scene.dir.write(path, buffer).then(
        () => {
            try {
                reportMinimapFileAccess(scene, path, size);
            } catch (error) {
                l.debug("Failed to report minimap file write:", path, error);
            }
        },
        (error: unknown) => {
            if (error instanceof DOMException && error.name === "QuotaExceededError") {
                writesDisabledByQuota = true;
                l.warn("Minimap OPFS writes disabled for the session: quota exceeded");
                return;
            }
            l.debug("Failed to write minimap file to OPFS:", path, error);
        },
    );
}

function flushPendingWrites(scene: OfflineScene): void {
    if (pendingWrites.size === 0) {
        return;
    }
    const toFlush = [...pendingWrites];
    pendingWrites.clear();
    for (const [path, buffer] of toFlush) {
        if (writesDisabledByQuota) {
            break;
        }
        performWrite(scene, path, buffer);
    }
}

/**
 * The path a tile-set file is journaled under inside the scene directory, or
 * `undefined` when it can't be stored safely.
 * @remarks
 * Relative paths come from the server's manifest (`tileUrlTemplate`, preview
 * `path`), so they are treated as untrusted text: a `..` segment could escape
 * the minimap subfolder, and a comma or control character would corrupt the
 * SDK's journal for every entry after it. Rejecting costs one file (network
 * fetch still works, just uncached); accepting could cost the journal.
 *
 * Deliberately not keyed by `sceneVersionId` on top of `fileInstanceId`:
 * `platformFileInstanceId` is understood to be immutable and unique per PDF
 * mapping on the Model Manager side -- re-mapping a sheet mints a new one
 * rather than regenerating a tile set under an existing id, so there's no
 * version to collide across. (Flagged in review; this understanding hasn't
 * been independently verified against Model Manager's own source, so treat
 * it as the team's current best knowledge, not a guarantee -- ask Model
 * Manager if this ever needs to be load-bearing for correctness elsewhere.)
 */
export function minimapFilePath(fileInstanceId: string, relativePath: string): string | undefined {
    if (!fileInstanceId || hasJournalUnsafeChars(fileInstanceId) || hasJournalUnsafeChars(relativePath)) {
        return undefined;
    }
    if (relativePath.startsWith("/")) {
        return undefined;
    }
    const segments = relativePath.split("/");
    if (segments.some((segment) => segment === "" || segment === "." || segment === "..")) {
        return undefined;
    }
    return `${MINIMAP_DIR_NAME}/${fileInstanceId}/${relativePath}`;
}

/** Read a tile-set file straight from OPFS, or `undefined` on a miss / when reading is unavailable. */
export async function readMinimapFileOffline(
    fileInstanceId: string,
    relativePath: string,
): Promise<ArrayBuffer | undefined> {
    if (!context?.readEnabled) {
        return undefined;
    }
    const path = minimapFilePath(fileInstanceId, relativePath);
    if (!path) {
        return undefined;
    }
    const scene = context.getScene();
    if (!scene) {
        return undefined;
    }
    flushPendingWrites(scene);
    try {
        const buffer = await scene.dir.read(path);
        if (buffer) {
            reportMinimapFileAccess(scene, path, buffer.byteLength);
        }
        return buffer;
    } catch (error) {
        l.debug("Failed to read minimap file from OPFS:", path, error);
        return undefined;
    }
}

/** Tee a freshly-fetched tile-set file into OPFS. Best-effort: never throws, never blocks the caller. */
export function writeMinimapFileOffline(fileInstanceId: string, relativePath: string, buffer: ArrayBuffer): void {
    if (!context?.writeEnabled || writesDisabledByQuota) {
        return;
    }
    const path = minimapFilePath(fileInstanceId, relativePath);
    if (!path) {
        return;
    }
    const scene = context.getScene();
    if (!scene) {
        // Scene not armed yet (`addScene` still in flight) -- queue instead
        // of dropping; flushed the next time a read or write finds a scene.
        if (!pendingWrites.has(path) && pendingWrites.size >= MAX_PENDING_WRITES) {
            const oldestPath = pendingWrites.keys().next().value as string;
            pendingWrites.delete(oldestPath);
        }
        pendingWrites.set(path, buffer);
        return;
    }
    flushPendingWrites(scene);
    performWrite(scene, path, buffer);
}
